import { combineActions, handleActions } from 'redux-actions';
import {
  ActionsEnum,
  IFetchEligibleClientsSuccess,
  IFetchReviewDetailSuccess,
  IFetchReviewsError,
  IFetchReviewSettingsSuccess,
  IFetchReviewsSuccess,
  IFetchReviewSummarySuccess,
  IRequestReviewError,
  IRequestReviewSuccess,
  ISaveReviewSettingsNetworkSuccess,
  ISendRequestReview,
  ISendRequestReviewError,
} from './review-data.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Review } from './models/review.model';
import { List } from 'immutable';
import { ReviewRequest } from './models/review-request.model';
import { ReviewClient } from './models/review-client.model';
import { ReviewSummary } from './models/review-summary.model';
import { IReviewSettings } from './review-data.data';

export const FEATURE_NAME = 'review-data';

export interface IState {
  reviews: List<Review>;
  clients: List<ReviewClient>;
  reviewRequest: ReviewRequest;
  requestReviewSending: boolean;
  reviewSummary: ReviewSummary;
  reviewDetail: Review;
  reviewSettings: IReviewSettings;
  error: Error;
}

// initial state
export const initialState: IState = {
  reviews: null,
  clients: null,
  reviewRequest: null,
  requestReviewSending: false,
  reviewSummary: null,
  reviewDetail: null,
  reviewSettings: null,
  error: null,
};

const reducerFn = handleActions(
  {
    [ActionsEnum.FETCH_REVIEWS_SUCCESS]: function (
      state: IState,
      { payload }: IFetchReviewsSuccess
    ): IState {
      return {
        ...state,
        reviews: payload.reviews,
        error: null,
      };
    },
    [ActionsEnum.FETCH_ELIGIBLE_CLIENTS_SUCCESS]: function (
      state: IState,
      { payload }: IFetchEligibleClientsSuccess
    ): IState {
      return {
        ...state,
        clients: payload.clients,
        error: null,
      };
    },
    [ActionsEnum.REQUEST_REVIEW_SUCCESS]: function (
      state: IState,
      { payload }: IRequestReviewSuccess
    ): IState {
      return {
        ...state,
        reviewRequest: payload.reviewRequest,
        error: null,
      };
    },
    [ActionsEnum.RESET_REQUEST_REVIEW]: function (state: IState): IState {
      return {
        ...state,
        reviewRequest: null,
      };
    },
    [ActionsEnum.SEND_REQUEST_REVIEW]: function (
      state: IState,
      { payload }: ISendRequestReview
    ): IState {
      return {
        ...state,
        requestReviewSending: true,
        error: null,
      };
    },
    [ActionsEnum.SEND_REQUEST_REVIEW_SUCCESS]: function (
      state: IState
    ): IState {
      return {
        ...state,
        requestReviewSending: false,
        error: null,
      };
    },
    [ActionsEnum.SEND_REQUEST_REVIEW_ERROR]: function (
      state: IState,
      { payload }: ISendRequestReviewError
    ): IState {
      return {
        ...state,
        requestReviewSending: false,
        error: payload.error,
      };
    },
    [ActionsEnum.FETCH_REVIEW_SUMMARY_SUCCESS]: function (
      state: IState,
      { payload }: IFetchReviewSummarySuccess
    ): IState {
      return {
        ...state,
        reviewSummary: payload.reviewSummary,
        error: null,
      };
    },
    [ActionsEnum.FETCH_REVIEW_DETAIL]: function (state: IState): IState {
      return {
        ...state,
        reviewDetail: null,
        error: null,
      };
    },
    [ActionsEnum.FETCH_REVIEW_DETAIL_SUCCESS]: function (
      state: IState,
      { payload }: IFetchReviewDetailSuccess
    ): IState {
      return {
        ...state,
        reviewDetail: payload.review,
        error: null,
      };
    },
    [ActionsEnum.FETCH_REVIEW_SETTINGS_SUCCESS]: function (
      state: IState,
      { payload }: IFetchReviewSettingsSuccess
    ): IState {
      return {
        ...state,
        reviewSettings: payload.reviewSettings,
        error: null,
      };
    },
    [ActionsEnum.SAVE_REVIEW_SETTINGS_NETWORK_SUCCESS]: function (
      state: IState,
      { payload }: ISaveReviewSettingsNetworkSuccess
    ): IState {
      return {
        ...state,
        reviewSettings: payload.reviewSettings,
        error: null,
      };
    },
    [combineActions(
      ActionsEnum.FETCH_REVIEWS_ERROR,
      ActionsEnum.FETCH_ELIGIBLE_CLIENTS_ERROR,
      ActionsEnum.REQUEST_REVIEW_ERROR,
      ActionsEnum.FETCH_REVIEW_SUMMARY_ERROR,
      ActionsEnum.FETCH_REVIEW_DETAIL_ERROR,
      ActionsEnum.FETCH_REVIEW_SETTINGS_ERROR,
      ActionsEnum.SAVE_REVIEW_SETTINGS_NETWORK_ERROR
    )]: function (
      state: IState,
      { payload }: IFetchReviewsError | IRequestReviewError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        error: error,
      };
    },
  } as any,
  initialState
);

export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const getReviews = createSelector(
  selectFeature,
  (state: IState) => state.reviews
);
export const getReviewRequest = createSelector(
  selectFeature,
  (state: IState) => state.reviewRequest
);
export const getEligibleClients = createSelector(
  selectFeature,
  (state: IState) => state.clients
);
export const getError = createSelector(
  selectFeature,
  (state: IState) => state.error
);
export const getRequestReviewSending = createSelector(
  selectFeature,
  (state: IState) => state.requestReviewSending
);
export const getReviewSummary = createSelector(
  selectFeature,
  (state: IState) => state.reviewSummary
);
export const getReviewDetail = createSelector(
  selectFeature,
  (state: IState) => state.reviewDetail
);

export const getReviewSettings = createSelector(
  selectFeature,
  (state: IState) => state.reviewSettings
);
